/**
 * Sign-up KYC review for the Annas page: node --test
 *
 * An Anna now registers, uploads his four documents, and presses Submit. The server
 * (firebase/functions/kycDocs.js) stamps drivers/{uid}.kycSubmittedAt, and, when switched on,
 * records an AI reading of each photo in drivers/{uid}.kycAi.{dl_front|rc|insurance}.
 *
 * Everything here is plain advice for the admin: who has submitted, what is missing or expired,
 * where the AI's reading differs from what the Anna typed. Nothing here approves anyone. These
 * rules mirror the server's submitDriverKyc and the Anna app, so the three agree.
 */

export const KYC_DOC_KEYS = ['drivingLicence', 'rc', 'insurance', 'aadhaar'];

export const KYC_DOC_LABEL = {
  drivingLicence: 'Driving Licence',
  rc: 'RC',
  insurance: 'Insurance',
  aadhaar: 'Aadhaar',
};

// The AI reads these photos only (both sides of the licence); Aadhaar is never read.
export const AI_SLOTS_FOR = { drivingLicence: ['dl_front', 'dl_back'], rc: ['rc'], insurance: ['insurance'] };

// Which validity to trust when two readings each found one: an explicit transport date first, then a
// plain "validity", then non-transport; the earlier date wins a tie (the cautious choice).
const KIND_RANK = { transport: 3, validity: 2, policy_end: 2, non_transport: 1 };
const SEVERITY = { ok: 0, check: 1, retake: 2 };

const SOON_DAYS = 30;

const DAY_MS = 86400000;

/** DD-MM-YYYY / DD/MM/YYYY / DD.MM.YYYY / YYYY-MM-DD to { d, m, y }, or null if not a real date. */
export function parseDate(raw) {
  const text = typeof raw === 'string' ? raw.trim() : '';
  let m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(text);
  let d; let mo; let y;
  if (m) {
    d = Number(m[1]); mo = Number(m[2]); y = Number(m[3]);
  } else {
    m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (!m) return null;
    y = Number(m[1]); mo = Number(m[2]); d = Number(m[3]);
  }
  if (y < 1990 || y > 2100 || mo < 1 || mo > 12 || d < 1) return null;
  const dim = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  if (d > dim) return null;
  return { d, m: mo, y };
}

const epochDay = ({ y, m, d }) => Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);

/** Today in India (UTC+5:30) as { d, m, y }. */
export function istToday(nowMs = Date.now()) {
  const ist = new Date(nowMs + 5.5 * 3600 * 1000);
  return { d: ist.getUTCDate(), m: ist.getUTCMonth() + 1, y: ist.getUTCFullYear() };
}

/** 'missing' | 'unreadable' | 'expired' | 'soon' | 'valid', with the days left when readable. */
export function expiryInfo(raw, nowMs = Date.now()) {
  if (raw === undefined || raw === null || String(raw).trim() === '') return { state: 'missing', days: null };
  const date = parseDate(raw);
  if (!date) return { state: 'unreadable', days: null };
  const days = epochDay(date) - epochDay(istToday(nowMs));
  if (days < 0) return { state: 'expired', days };
  return { state: days <= SOON_DAYS ? 'soon' : 'valid', days };
}

/**
 * For an expiry that is not a real date as stored, a likely reading when the mistake is obvious:
 * eight digits with no separators ("20122026" = 20-12-2026) or a date with stray letters after it
 * ("10/03/2034ve" = 10-03-2034). Returns "DD-MM-YYYY" or ''. Advice only: nothing is changed.
 */
export function suggestDate(raw) {
  const t = typeof raw === 'string' ? raw.trim() : '';
  if (!t || parseDate(t)) return '';
  let candidate = '';
  if (/^\d{8}$/.test(t)) candidate = `${t.slice(0, 2)}-${t.slice(2, 4)}-${t.slice(4)}`;
  else {
    const m = /^(\d{1,2}[-/.]\d{1,2}[-/.]\d{4})\D+$/.exec(t);
    if (m) candidate = m[1];
  }
  const d = parseDate(candidate);
  return d ? `${String(d.d).padStart(2, '0')}-${String(d.m).padStart(2, '0')}-${d.y}` : '';
}

const sameDate = (a, b) => {
  const x = parseDate(a);
  const y = parseDate(b);
  return Boolean(x && y && x.d === y.d && x.m === y.m && x.y === y.y);
};

const squash = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const hasText = (v) => typeof v === 'string' && v.trim() !== '';

/** Milliseconds of a Firestore Timestamp / {seconds} / number, or 0. */
export function stampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value;
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
}

/** 'approved' | 'submitted' | 'not_submitted'. A blocked Anna is still judged by his documents. */
export function submissionState(driver) {
  if (driver?.isApproved) return 'approved';
  return stampMillis(driver?.kycSubmittedAt) > 0 ? 'submitted' : 'not_submitted';
}

function entryOf(driver, key) {
  const docs = driver?.documents && typeof driver.documents === 'object' ? driver.documents : {};
  return docs[key] && typeof docs[key] === 'object' ? docs[key] : {};
}

const photoOf = (key, e) => (key === 'drivingLicence' ? e.frontPhotoUrl : e.photoUrl);
const numberOf = (key, e) => {
  if (key === 'insurance') return e.policyNumber;
  if (key === 'aadhaar') return e.last4;
  return e.number;
};

/** Is this one mandatory document ready, by the same rule the server applies on Submit? (The licence, the RC and the Aadhaar each need BOTH sides.) */
export function documentReady(driver, key, nowMs = Date.now()) {
  const e = entryOf(driver, key);
  const photo = hasText(photoOf(key, e)) && (key === 'insurance' || hasText(e.backPhotoUrl));
  const number = key === 'aadhaar' ? /^\d{4}$/.test(String(e.last4 || '')) : hasText(numberOf(key, e));
  if (!photo || !number) return false;
  if (key === 'drivingLicence' || key === 'insurance') {
    return ['valid', 'soon'].includes(expiryInfo(e.expiryDate, nowMs).state);
  }
  return true;
}

/** Mandatory documents that are not ready (photo, number, or an unexpired date is missing). */
export function missingDocuments(driver, nowMs = Date.now()) {
  return KYC_DOC_KEYS.filter((key) => !documentReady(driver, key, nowMs));
}

/**
 * A sentence for the "Approve anyway?" confirmation, or '' when the Anna's file is in order.
 * Approving is still allowed: Annas who signed up on an older app cannot submit documents.
 */
export function approvalWarning(driver, nowMs = Date.now()) {
  const lines = [];
  if (submissionState(driver) === 'not_submitted') {
    lines.push('He has not submitted his documents (in progress, or signed up on an older app).');
  }
  const missing = missingDocuments(driver, nowMs);
  if (missing.length) {
    lines.push(`Missing or expired: ${missing.map((k) => KYC_DOC_LABEL[k]).join(', ')}.`);
  }
  return lines.join('\n');
}

/** One AI result for a document made from its readings (the licence has two: front and back). */
function combineReadings(records) {
  const rows = records.filter((r) => r && typeof r === 'object');
  if (!rows.length) return null;
  const fieldsOf = (r) => (r.fields && typeof r.fields === 'object' ? r.fields : {});
  const known = rows.map((r) => r.status).filter((x) => x in SEVERITY);
  const status = known.length ? known.sort((a, b) => SEVERITY[b] - SEVERITY[a])[0] : 'unchecked';
  const withDate = rows
    .map((r) => ({ iso: String(fieldsOf(r).expiryDate || ''), kind: String(r.expiryKind || fieldsOf(r).expiryKind || ''), label: String(r.expiryLabel || '') }))
    .filter((x) => x.iso);
  withDate.sort((a, b) => (KIND_RANK[b.kind] || 0) - (KIND_RANK[a.kind] || 0) || a.iso.localeCompare(b.iso));
  const first = (name) => rows.map((r) => String(fieldsOf(r)[name] || '')).find(Boolean) || '';
  return {
    status,
    issues: [...new Set(rows.flatMap((r) => (Array.isArray(r.issues) ? r.issues.filter((i) => typeof i === 'string') : [])))],
    number: first('number'),
    expiryDate: withDate[0]?.iso || '',
    expiryKind: withDate[0]?.kind || '',
    expiryLabel: withDate[0]?.label || '',
    name: first('name'),
    vehicleNumber: first('vehicleNumber'),
    sides: rows.length,
  };
}

/**
 * Everything the review card shows for one document: what the Anna entered, what the AI read
 * (if it ran), and where they differ. [flags] are short codes the screen turns into words.
 */
export function documentReview(driver, key, nowMs = Date.now()) {
  const e = entryOf(driver, key);
  const kycAi = driver?.kycAi && typeof driver.kycAi === 'object' ? driver.kycAi : {};
  const ai = combineReadings((AI_SLOTS_FOR[key] || []).map((slot) => kycAi[slot]));
  const number = key === 'aadhaar' ? (e.last4 ? `••••${e.last4}` : '') : String(numberOf(key, e) || '');
  const expiryDate = key === 'drivingLicence' || key === 'insurance' ? String(e.expiryDate || '') : '';
  const expiry = key === 'drivingLicence' || key === 'insurance'
    ? expiryInfo(expiryDate, nowMs)
    : { state: 'n/a', days: null };

  const flags = [];
  if (expiry.state === 'expired') flags.push('expired');
  if (expiry.state === 'soon') flags.push('expiring_soon');
  if (expiry.state === 'unreadable') flags.push('expiry_unreadable');

  if (ai && ai.number && number && key !== 'aadhaar' && squash(ai.number) !== squash(number)) flags.push('number_differs_from_ai');
  if (ai && ai.expiryDate && expiryDate && !sameDate(ai.expiryDate, expiryDate)) flags.push('expiry_differs_from_ai');
  if (ai && key === 'drivingLicence' && ai.expiryKind === 'non_transport') flags.push('non_transport_validity_only');

  return {
    key,
    label: KYC_DOC_LABEL[key],
    ready: documentReady(driver, key, nowMs),
    hasPhoto: hasText(photoOf(key, e)),
    hasBackPhoto: key === 'insurance' ? null : hasText(e.backPhotoUrl),
    status: e.status || 'not_uploaded',
    number,
    expiryDate,
    expiry,
    expirySuggestion: expiry.state === 'unreadable' ? suggestDate(expiryDate) : '',
    ai,
    flags,
  };
}

export const AI_ISSUE_TEXT = {
  blurry: 'Photo is blurry',
  cropped: 'Part of the document is cut off',
  glare: 'Glare over the text',
  dark: 'Photo is too dark',
  wrong_document: 'Not the right kind of document',
  not_a_document: 'Not a document',
  expired: 'Document has expired',
  expiring_soon: 'Expires within 30 days',
  number_mismatch: "Number differs from the registration form",
  name_mismatch: "Name differs from the registration form",
  unreadable_expiry: 'Expiry date could not be read',
};

export const KIND_TEXT = {
  transport: 'Transport validity',
  validity: 'Validity (card does not say which)',
  non_transport: 'Non-transport validity',
  policy_end: 'End of policy',
};

export const FLAG_TEXT = {
  non_transport_validity_only: 'Only a non-transport validity was read',
  expired: 'Expired',
  expiring_soon: 'Expires within 30 days',
  expiry_unreadable: 'Expiry date is not a real date',
  number_differs_from_ai: 'Number differs from what the AI read',
  expiry_differs_from_ai: 'Expiry differs from what the AI read',
};

/** The photo source written by uploadDriverPhotoSecure ('camera' | 'gallery'), from Storage metadata JSON. */
export function photoSourceFromMetadata(meta) {
  const v = meta && meta.metadata && meta.metadata.photoSource;
  return v === 'camera' || v === 'gallery' ? v : '';
}

/** URL of the object's metadata JSON for a tokenised download URL (same URL without alt=media). */
export function metadataUrl(downloadUrl) {
  if (typeof downloadUrl !== 'string' || !/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\//.test(downloadUrl)) return '';
  try {
    const u = new URL(downloadUrl);
    u.searchParams.delete('alt');
    return u.toString();
  } catch {
    return '';
  }
}

/**
 * Review-queue order: submitted applications first, oldest submission first (first come, first
 * served), then everyone else with the most recent activity first. Returns a new array.
 */
export function sortForReview(drivers, activityMillis = () => 0) {
  return [...drivers].sort((a, b) => {
    const sa = stampMillis(a?.kycSubmittedAt);
    const sb = stampMillis(b?.kycSubmittedAt);
    if (sa && sb) return sa - sb;
    if (sa) return -1;
    if (sb) return 1;
    return activityMillis(b) - activityMillis(a);
  });
}
